import { memo, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import styled from 'styled-components';
import maplibregl, { LngLatBounds } from 'maplibre-gl';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { MAPTILER_API_KEY } from '~/config/config';
import { isMapLoadedDoneAtom, showMinimapAtom } from '../stores/mapStore';

interface MinimapProps {
  className?: string;
}

function Minimap({ className }: MinimapProps) {
  const minimapContainer = useRef<HTMLDivElement>(null);
  const minimap = useRef<maplibregl.Map | null>(null);
  const mainMapViewportBox = useRef<maplibregl.Marker | null>(null);
  const projectBoundsBox = useRef<any>(null);

  const currentProject = useAtomValue(currentProjectAtom);
  const isMapLoadedDone = useAtomValue(isMapLoadedDoneAtom);
  const [showMinimap, setShowMinimap] = useAtom(showMinimapAtom);

  const [isMinimapLoaded, setIsMinimapLoaded] = useState(false);

  // Initialize minimap
  useEffect(() => {
    if (!minimapContainer.current || minimap.current) return;

    minimap.current = new maplibregl.Map({
      container: minimapContainer.current,
      style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_API_KEY}`,
      center: [20.83416, 10.027764],
      zoom: 2,
      attributionControl: false,
      dragPan: false,
      scrollZoom: false,
      boxZoom: false,
      dragRotate: false,
      doubleClickZoom: false,
      touchZoomRotate: false,
      interactive: false
    });

    minimap.current.on('load', () => {
      setIsMinimapLoaded(true);
    });

    return () => {
      if (minimap.current) {
        minimap.current.remove();
        minimap.current = null;
      }
    };
  }, []);

  // Display project bounding box
  useEffect(() => {
    if (!minimap.current || !isMinimapLoaded || !currentProject?.boundingBox) return;

    try {
      // Remove existing project bounds
      if (projectBoundsBox.current) {
        minimap.current.removeSource('project-bounds');
        minimap.current.removeLayer('project-bounds-fill');
        minimap.current.removeLayer('project-bounds-line');
        projectBoundsBox.current = null;
      }

      const boundsCoords = JSON.parse(currentProject.boundingBox);
      const bounds = new LngLatBounds(boundsCoords);

      // Fit minimap to project bounds with padding
      minimap.current.fitBounds(bounds, { padding: 20 });

      // Add project bounds as a rectangle
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const nw = bounds.getNorthWest();
      const se = bounds.getSouthEast();

      const boundsGeoJSON = {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            [sw.lng, sw.lat],
            [se.lng, se.lat],
            [ne.lng, ne.lat],
            [nw.lng, nw.lat],
            [sw.lng, sw.lat]
          ]]
        }
      };

      minimap.current.addSource('project-bounds', {
        type: 'geojson',
        data: boundsGeoJSON
      });

      minimap.current.addLayer({
        id: 'project-bounds-fill',
        type: 'fill',
        source: 'project-bounds',
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.1
        }
      });

      minimap.current.addLayer({
        id: 'project-bounds-line',
        type: 'line',
        source: 'project-bounds',
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2,
          'line-opacity': 0.8
        }
      });

      projectBoundsBox.current = true;
    } catch (error) {
      console.error('Error displaying project bounds on minimap:', error);
    }
  }, [currentProject?.boundingBox, isMinimapLoaded]);

  // Update main map viewport indicator
  useEffect(() => {
    if (!minimap.current || !isMinimapLoaded || !window.map || !isMapLoadedDone) return;

    const updateViewport = () => {
      if (!minimap.current || !window.map) return;

      try {
        // Remove existing viewport marker
        if (mainMapViewportBox.current) {
          mainMapViewportBox.current.remove();
          mainMapViewportBox.current = null;
        }

        const mainBounds = window.map.getBounds();
        const center = mainBounds.getCenter();

        // Create a custom element for the viewport box
        const viewportElement = document.createElement('div');
        viewportElement.style.width = '20px';
        viewportElement.style.height = '20px';
        viewportElement.style.backgroundColor = '#ef4444';
        viewportElement.style.border = '2px solid #dc2626';
        viewportElement.style.borderRadius = '3px';
        viewportElement.style.opacity = '0.8';
        viewportElement.style.cursor = 'pointer';

        // Add marker for main map viewport
        mainMapViewportBox.current = new maplibregl.Marker({
          element: viewportElement,
          anchor: 'center'
        })
          .setLngLat([center.lng, center.lat])
          .addTo(minimap.current);

      } catch (error) {
        console.error('Error updating minimap viewport:', error);
      }
    };

    // Initial update
    updateViewport();

    // Listen to main map changes
    const handleMapMove = () => {
      updateViewport();
    };

    window.map.on('moveend', handleMapMove);
    window.map.on('zoomend', handleMapMove);

    return () => {
      if (window.map) {
        window.map.off('moveend', handleMapMove);
        window.map.off('zoomend', handleMapMove);
      }
    };
  }, [isMinimapLoaded, isMapLoadedDone]);

  if (!showMinimap) {
    return null;
  }

  return (
    <MinimapContainer className={className}>
      <CloseButton onClick={() => setShowMinimap(false)}>
        ×
      </CloseButton>
      <MinimapWrapper ref={minimapContainer} />
    </MinimapContainer>
  );
}

export default memo(Minimap);

const MinimapContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: 200px;
  height: 150px;
  z-index: 100;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.8);
  background: white;
  pointer-events: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  z-index: 1001;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MinimapWrapper = styled.div`
  width: 100%;
  height: 100%;

  .maplibregl-canvas {
    border-radius: 6px;
  }
`;